import React, { useEffect, useMemo, useState } from 'react';
import { Box, FlexControlledForm } from '@common-fe/common-fe';

import { StatementFeePaidByType, StatementSettings, StatementType } from '@/modules/employer/components/SetupEmployer/Communication/Communication.types';
import { useStatementsSettingsStore } from '@/modules/employer/components/SetupEmployer/Communication/stores';
import FormLoader from '@/modules/employer/components/SetupEmployer/FormLoader';
import { useSetupEmployerMode } from '@/modules/employer/components/SetupEmployer/hooks';
import useGetEmployer from '@/modules/employer/hooks/useGetEmployer.query';

import { useSaveTypeStore } from '../../stores';
import { useStatementsSettingsFields } from '../hooks';
import usePaperStatementsFeesFields from '../hooks/usePaperStatementsFeesFields';

interface Props {
  onDirty?: (isDirty: boolean) => void;
  isPartnerPage?: boolean;
}

const StatementsSettings: React.FC<Props> = ({ onDirty, isPartnerPage }) => {
  const { viewMode } = useSetupEmployerMode();
  const { formattedData, isLoading } = useGetEmployer();
  // const { values, setValues } = useDebounceState<StatementSettings>();
  // const { values: feesValues, setValues: setFeesValues } = useDebounceState<StatementSettings>();
  const [values, setValues] = useState<StatementSettings>();
  const [feesValues, setFeesValues] = useState<StatementSettings>();

  const { activeStatus, pendingStatus } = useSaveTypeStore();
  const handleSetValues = useStatementsSettingsStore((state) => state.handleSetValues);
  const state = useStatementsSettingsStore((state) => state.state);

  const isPaperStatementsFeesEnabled = useMemo(
    () => values?.statementType === StatementType.PAPER_AND_ELECTRONIC,
    [values]);
  const isAdminFees = useMemo(
    () => feesValues?.paperStatementFeesPaidBy === StatementFeePaidByType.ADMIN,
    [feesValues],
  );

  const fields = useStatementsSettingsFields(formattedData);
  const feesFields = usePaperStatementsFeesFields(formattedData, isAdminFees);

  useEffect(() => {
    if (values) {
      handleSetValues({
        ...state,
        ...values,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, handleSetValues]);
  useEffect(() => {
    if (feesValues) {
      handleSetValues({
        ...state,
        ...feesValues,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feesValues, handleSetValues]);

  if (isLoading) return <FormLoader testId="statements-form" />;
  if(!fields.length) {
    return null;
  }
  return (
    <Box
      background="canvas"
      round="container1Round"
      margin={{ top: isPartnerPage ? 'spacing24' : '0' }}
      border={{ color: 'border2', size: 'small' }}
      id="statements"
      data-testid="statements-settings"
    >
      <Box>
        <FlexControlledForm
          formTitle="Statements"
          fields={fields}
          onDirty={onDirty}
          onChangeValues={setValues}
          editMode={!viewMode}
          formStyle={(isPaperStatementsFeesEnabled) ? { marginBottom: 0 } : {}}
          wrapperStyle={{ border: 'none' }}
          isThinMode
        />
      </Box>
      {(isPaperStatementsFeesEnabled) ? (
        <Box>
          <FlexControlledForm
            formTitle="Paper Statements Fees"
            fields={feesFields}
            onDirty={onDirty}
            onChangeValues={setFeesValues}
            editMode={!viewMode}
            wrapperStyle={{ border: 'none' }}
            showError={activeStatus || pendingStatus}
            isThinMode
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default StatementsSettings;
